import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { VariableSizeList } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import { useDebouncedValue } from "../../../common/helperfunctions/useDebouncedValue";
import { useInfiniteListEntity } from "../../../api/BaseEntityApi";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import { AutoSizer } from "react-virtualized";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import styles from "./CustomFieldCheckboxSelectForm.module.css";
import { CheckboxRound } from "../../../common/formfields/CheckboxFormField/CheckboxFormField";
import { useResizeDetector } from "react-resize-detector";
import {
  addOrRemoveValue,
  addValues,
  removeValues,
} from "../../../common/hierarchy/HierarchySelectForm/HierarchySelectForm";
import { abstractedDataTypeToIconName } from "../../CustomFields/CustomFieldRenderUtils";
import { generateAbstractedFromDataType } from "../../CustomFields/CustomFieldUtils";
import { CustomField } from "../../../api/CustomFields/CustomFields";
import {
  CustomTypeSection,
  CustomType,
  CustomTypeFilters,
  customTypeConstants,
} from "../../../api/CustomTypes/CustomTypes";

interface FoldingStatus {
  [customTypeId: number]: {
    show: boolean;
    sections: {
      [sectionName: string]: boolean;
    };
  };
}

const dataTypeBlackList: readonly CustomField["dataType"][] = ["Barcode", "Attachment"] as const;

const customTypeSectionToCustomFields = (section: CustomTypeSection): CustomField[] => {
  return section.customFields.filter((field) => !dataTypeBlackList.includes(field.dataType)).map((c) => c);
};

const customTypeToCustomFields = (customType: CustomType): CustomField[] => {
  return customType.sections.map((section) => customTypeSectionToCustomFields(section)).flat();
};

const defaultRowHeight = 30;

interface CustomFieldCheckboxSelectFormProps {
  filters?: CustomTypeFilters;
  value?: CustomField | CustomField[] | null | undefined;
  onChange: (values: CustomField | CustomField[] | undefined | null) => void;
  isMulti?: boolean;
}
export const CustomFieldCheckboxSelectForm = ({
  filters,
  value,
  onChange,
  isMulti,
}: CustomFieldCheckboxSelectFormProps) => {
  const virtualLoaderGrid = useRef<InfiniteLoader & { _listRef: VariableSizeList }>(null);
  const heights = useRef<{ [index: number]: number }>({});
  const [items, setItems] = useState<CustomType[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchValue = useDebouncedValue(searchValue, 500);
  const [foldingStatus, setFoldingStatus] = useState<FoldingStatus>({});

  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteListEntity<CustomType, CustomTypeFilters>(
    customTypeConstants.resource,
    {
      page: 1,
      pageSize: 10,
      includeCount: false,
      ...filters,
      ...(debouncedSearchValue && { searchTerm: debouncedSearchValue }),
      excludeEmptyCustomTypes: true,
    },
    { enabled: true },
    "post"
  );

  const onChangeCallback = useCallback(
    (values: CustomField | CustomField[] | null | undefined) => {
      onChange?.(isMulti ? values ?? [] : Array.isArray(values) ? values?.pop() : values);
      virtualLoaderGrid.current?._listRef.forceUpdate();
    },
    [isMulti, onChange]
  );

  useEffect(() => {
    if (data) setItems(data.pages.map((d) => d.results).flat());
  }, [data]);

  const fetchNext = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const loadMoreCallback = useCallback(() => {}, []);
  const itemCount = hasNextPage ? items.length + 1 : items.length;
  const loadMoreItems = isFetching ? loadMoreCallback : fetchNext;
  const isItemLoaded = useCallback((index: number) => !!items?.[index], [items]);

  const onHeightChange = useCallback((index: number, height: number) => {
    heights.current[index] = height;
    virtualLoaderGrid.current?._listRef.resetAfterIndex(index, true);
  }, []);

  const Item = ({ index, style }: { index: number; style: CSSProperties }) => {
    if (!isItemLoaded(index)) {
      return (
        <div style={{ ...style }} key={index}>
          <span className="skeleton-block" />
        </div>
      );
    }
    const item = items[index];
    const customFields = customTypeToCustomFields(item);
    const customFieldIds = customFields.map((field) => field.id);

    const hasCustomFields = value
      ? isMulti && Array.isArray(value)
        ? !!value?.some((v) => customFieldIds.includes(v.id))
        : customFieldIds.includes((value as CustomField)?.id)
      : false;
    const checked = value
      ? !!customFieldIds.length && isMulti
        ? customFieldIds.every((id) => (value as CustomField[])?.some((v) => v.id === id))
        : customFieldIds.every((id) => (value as CustomField).id === id)
      : false;
    const indeterminate = !checked && hasCustomFields;

    // const isSelected = hasValue(value, item.id);
    return (
      <CustomTypeCheckboxRenderer
        index={index}
        style={style}
        entity={item}
        checked={checked}
        indeterminate={indeterminate}
        value={value}
        onChange={onChangeCallback}
        customFields={customFields}
        onHeightChange={onHeightChange}
        foldingStatus={foldingStatus}
        setFoldingStatus={setFoldingStatus}
        isMulti={isMulti}
      />
    );
  };

  return (
    <div className="flex col-nowrap gap-5" style={{ width: "100%", height: "100%" }}>
      <SearchInput
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        placeholder={`Search ${customTypeConstants.entityPlural}`}
      />
      <div className="flex col-nowrap" style={{ width: "100%", height: "100%" }}>
        {!!items.length && (
          <InfiniteLoader
            ref={virtualLoaderGrid}
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={loadMoreItems as any}
          >
            {({ onItemsRendered, ref }) => (
              <AutoSizer>
                {({ width, height }) => (
                  <VariableSizeList
                    itemCount={itemCount}
                    onItemsRendered={onItemsRendered}
                    ref={ref}
                    width={width}
                    height={height}
                    itemSize={(index) => heights.current?.[index] || defaultRowHeight}
                  >
                    {Item}
                  </VariableSizeList>
                )}
              </AutoSizer>
            )}
          </InfiniteLoader>
        )}
      </div>
    </div>
  );
};

interface CustomTypeCheckboxRendererProps {
  index: number;
  style: CSSProperties;
  entity: CustomType;
  checked: boolean;
  indeterminate: boolean;
  value: CustomFieldCheckboxSelectFormProps["value"];
  onChange: CustomFieldCheckboxSelectFormProps["onChange"];
  customFields: CustomField[];
  onHeightChange: (index: number, height: number) => void;
  foldingStatus: FoldingStatus;
  setFoldingStatus: React.Dispatch<React.SetStateAction<FoldingStatus>>;
  isMulti: CustomFieldCheckboxSelectFormProps["isMulti"];
}
const CustomTypeCheckboxRenderer = ({
  index,
  style,
  entity,
  checked,
  indeterminate,
  value,
  onChange,
  customFields,
  onHeightChange,
  foldingStatus,
  setFoldingStatus,
  isMulti,
}: CustomTypeCheckboxRendererProps) => {
  const { ref, height } = useResizeDetector({ onResize: () => {} });

  useEffect(() => {
    if (!!height) onHeightChange(index, height);
    // else {
    //   console.log("Derendering");
    //   onHeightChange(index, defaultRowHeight);
    // }
  }, [height, index, onHeightChange, ref]);

  const show = !!foldingStatus?.[entity.id]?.show;

  return (
    <div className="flex col-nowrap" style={{ width: "100%", ...style, height: "min-content" }} key={index} ref={ref}>
      <div className={`${styles.row} ${styles.rowBase} ${checked && styles.rowChecked}`}>
        <div
          className={styles.rowControls}
          onClick={() => {
            setFoldingStatus((prev) => ({
              ...prev,
              [entity.id]: {
                ...prev?.[entity.id],
                show: !!!prev?.[entity.id]?.show,
              },
            }));
          }}
        >
          {show ? <LucideIcon name="chevron-up" /> : <LucideIcon name="chevron-down" />}
        </div>
        <div
          className={`${styles.rowLabel} ellipsisContainer`}
          onClick={() => {
            if (isMulti)
              onChange?.(
                indeterminate
                  ? removeValues(customFields, value as CustomField[])
                  : checked
                  ? removeValues(customFields, value as CustomField[])
                  : addValues(customFields, value as CustomField[])
              );
          }}
        >
          <span>
            <LucideIcon name={customTypeConstants.icon} color={"var(--primary)"} /> {entity.name}
          </span>
        </div>
        <div className={styles.rowCheckbox}>
          {!!customFields.length && (
            <CheckboxRound
              checked={checked}
              indeterminate={indeterminate}
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange?.(
                  indeterminate
                    ? removeValues(customFields, value as CustomField[])
                    : checked
                    ? removeValues(customFields, value as CustomField[])
                    : addValues(customFields, value as CustomField[])
                );
              }}
              disabled={!isMulti}
            />
          )}
        </div>
      </div>
      {show && (
        <div className="flex col-nowrap" style={{ width: "100%", height: show ? "min-content" : 0 }}>
          {entity.sections.map((section, sectionIndex) => {
            const sectionCustomFields = customTypeSectionToCustomFields(section);
            const checkedSection = isMulti
              ? sectionCustomFields.every((field) => (value as CustomField[])?.some((v) => v.id === field.id))
              : sectionCustomFields.every((field) => (value as CustomField)?.id === field.id);
            const indeterminateSection =
              !checkedSection &&
              (isMulti
                ? sectionCustomFields.some((field) => (value as CustomField[])?.some((v) => v.id === field.id))
                : sectionCustomFields.some((field) => (value as CustomField)?.id === field.id));
            const showSection = !!foldingStatus?.[entity.id]?.sections?.[section.name];
            return (
              <div className={styles.section} key={sectionIndex}>
                <div className={styles.rowBase}>
                  <div
                    className={styles.rowControls}
                    onClick={() => {
                      setFoldingStatus((prev) => ({
                        ...prev,
                        [entity.id]: {
                          ...prev?.[entity.id],
                          sections: {
                            ...prev?.[entity.id]?.sections,
                            [section.name]: !!!prev?.[entity.id]?.sections?.[section.name],
                          },
                        },
                      }));
                    }}
                  >
                    {showSection ? <LucideIcon name="chevron-up" /> : <LucideIcon name="chevron-down" />}
                  </div>
                  <div
                    className={`${styles.rowLabel} ${styles.sectionLabel} ellipsisContainer`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isMulti)
                        onChange?.(
                          checkedSection
                            ? removeValues(
                                sectionCustomFields,
                                isMulti ? (value as CustomField[]) : ([value] as CustomField[])
                              )
                            : addValues(
                                sectionCustomFields,
                                isMulti ? (value as CustomField[]) : ([value] as CustomField[])
                              )
                        );
                    }}
                  >
                    <span>
                      <LucideIcon name="panel-top-dashed" /> Section: {section.name}
                    </span>
                  </div>
                  <div className={styles.rowCheckbox}>
                    <CheckboxRound
                      checked={checkedSection}
                      indeterminate={indeterminateSection}
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onChange?.(
                          indeterminateSection
                            ? removeValues(
                                sectionCustomFields,
                                isMulti ? (value as CustomField[]) : ([value] as CustomField[])
                              )
                            : checkedSection
                            ? removeValues(
                                sectionCustomFields,
                                isMulti ? (value as CustomField[]) : ([value] as CustomField[])
                              )
                            : addValues(
                                sectionCustomFields,
                                isMulti ? (value as CustomField[]) : ([value] as CustomField[])
                              )
                        );
                      }}
                      disabled={!isMulti}
                    />
                  </div>
                </div>
                {showSection &&
                  section.customFields.map((field, fieldIndex) => {
                    return (
                      <div
                        key={fieldIndex}
                        className={`${styles.fieldRow} ${styles.rowBase} ${
                          (isMulti && value
                            ? !!(value as CustomField[])?.some((v) => v.id === field.id)
                            : value && (value as CustomField).id === field.id) && styles.rowChecked
                        } `}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onChange?.(
                            addOrRemoveValue(
                              field,
                              isMulti ? ((value ?? []) as CustomField[]) : ((value ? [value] : []) as CustomField[])
                            )
                          );
                        }}
                      >
                        <div className={`${styles.fieldLabel} ellipsisContainer`}>
                          <span>
                            <LucideIcon
                              name={abstractedDataTypeToIconName(
                                generateAbstractedFromDataType(field.dataType).abstractedDataType
                              )}
                              color={"var(--primary)"}
                            />{" "}
                            {field.name}
                          </span>
                        </div>
                        <div className={styles.rowCheckbox}>
                          <CheckboxRound
                            checked={
                              value
                                ? isMulti
                                  ? !!(value as CustomField[])?.some((v) => v.id === field.id)
                                  : (value as CustomField).id === field.id
                                : false
                            }
                            onChange={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              onChange?.(
                                addOrRemoveValue(field, isMulti ? (value as CustomField[]) : ([value] as CustomField[]))
                              );
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
